// API Base URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

// Authentication endpoints
export const AUTH_ENDPOINTS = {
  ADMIN_LOGIN: "/auth/login/admin",
  OTP_LOGIN: "/auth/login",
  CHANGE_PASSWORD: "/auth/admin/change-password",
  SIGNUP: "/auth/signup",
  VERIFY_OTP: "/auth/verify",
  RESEND_OTP: "/auth/resend",
  EMAIL_VERIFY: "/auth/email/verify",
  PHONE_SEND_OTP: "/auth/phone/send-otp",
  PHONE_VERIFY: "/auth/phone/verify",
  PHONE_CHECK_AVAILABILITY: "/auth/phone/check-availability",
  VERIFICATION_STATUS: "/auth/verification-status",
};

// Lead Management endpoints (renamed from OTP)
export const LEAD_ENDPOINTS = {
  LIST: "/leads/",
  CREATE: "/leads/",
  SIGNUP: "/leads/signup",
  UPDATE: (id: string) => `/leads/${id}`,
  DELETE: (id: string) => `/leads/${id}`,
  GET_AGENTS: (id: string) => `/leads/${id}/agents`,
  ADD_AGENT: (id: string) => `/leads/${id}/agents`,
  REMOVE_AGENT: (id: string, agentUuid: string) =>
    `/leads/${id}/agents/${agentUuid}`,
};

// Campaign endpoints
export const CAMPAIGN_ENDPOINTS = {
  LIST: "/campaigns",
  CREATE: "/campaigns",
  GET: (id: string) => `/campaigns/${id}`,
  UPDATE: (id: string) => `/campaigns/${id}`,
  DELETE: (id: string) => `/campaigns/${id}`,
  UPLOAD_CONTACTS: (id: string) => `/campaigns/${id}/upload-contacts`,
  CONTACTS: (id: string) => `/campaigns/${id}/contacts`,
  START: (id: string) => `/campaigns/${id}/start`,
  PAUSE: (id: string) => `/campaigns/${id}/pause`,
  STOP: (id: string) => `/campaigns/${id}/stop`,
  STATS: (id: string) => `/campaigns/${id}/stats`,
  DASHBOARD_STATS: "/campaigns/dashboard/stats",
  ADD_LEAD: (campaignId: string, leadId: number) => `/campaigns/${campaignId}/add-lead/${leadId}`,
  REMOVE_LEAD: (campaignId: string, leadId: number) => `/campaigns/${campaignId}/remove-lead/${leadId}`,
  GET_LEADS: (campaignId: string) => `/campaigns/${campaignId}/leads`,
};

// Conversations endpoints
export const CONVERSATIONS_ENDPOINTS = {
  LIST: "/conversations",
  DELETE: (id: string) => `/conversations/${id}`,
  TOGGLE_PIN: (id: string) => `/conversations/${id}/pin`,
  UNKNOWN: "/conversations/unknown",
};

// Agent endpoints
export const AGENT_ENDPOINTS = {
  LIST: "/agents/list",
  ACCESS: (uuid: string) => `/agents/access/${uuid}`,
  UPLOAD: (uuid: string) => `/agents/upload/${uuid}`,
  PHONE_CALL: "/agents/phone_call",
  PHONE_CALL_PROVIDER_CHECK: "/agents/phone_call/provider-check",
  VALIDATE_TOKEN: "/agents/validate-token",
};

// Admin Agent endpoints
export const ADMIN_AGENT_ENDPOINTS = {
  LIST: "/admin/agents/",
  CREATE: "/admin/agents/",
  GET: (uuid: string) => `/admin/agents/${uuid}`,
  UPDATE: (uuid: string) => `/admin/agents/${uuid}`,
  DELETE: (uuid: string) => `/admin/agents/${uuid}`,
  ENABLE: (uuid: string) => `/admin/agents/${uuid}/enable`,
  DISABLE: (uuid: string) => `/admin/agents/${uuid}/disable`,
  ENABLE_IS_PUBLIC: (uuid: string) => `/admin/agents/${uuid}/is_public/enable`,
  DISABLE_IS_PUBLIC: (uuid: string) =>
    `/admin/agents/${uuid}/is_public/disable`,
  ENABLE_CAN_DISCONNECT: (uuid: string) =>
    `/admin/agents/${uuid}/can_disconnect/enable`,
  DISABLE_CAN_DISCONNECT: (uuid: string) =>
    `/admin/agents/${uuid}/can_disconnect/disable`,
  ENABLE_PHONE_CALL: (uuid: string) =>
    `/admin/agents/${uuid}/phone_call/enable`,
  DISABLE_PHONE_CALL: (uuid: string) =>
    `/admin/agents/${uuid}/phone_call/disable`,
  DUPLICATE: (uuid: string) => `/admin/agents/${uuid}/duplicate`,
  CREATE_DISPOSABLE: (uuid: string) => `/admin/agents/${uuid}/disposable`,
  PROVIDER_MANIFEST: "/admin/agents/provider-manifest",
};

export const PROVIDER_LATENCY_ENDPOINTS = {
  CONFIG: "/admin/provider-latency/config",
  RESULTS: "/admin/provider-latency/results",
  RUN_NOW: "/admin/provider-latency/run-now",
};

// Agent evaluation endpoints
export const AGENT_EVALUATION_ENDPOINTS = {
  AGENT: (agentUuid: string) =>
    `/admin/agents/${agentUuid}/evaluations/agent`,
  PROVIDER_MANIFEST: (agentUuid: string) =>
    `/admin/agents/${agentUuid}/evaluations/provider-manifest`,
  SUITE: (uuid: string) => `/admin/agents/${uuid}/evaluations/suite`,
  SCENARIOS: (uuid: string) => `/admin/agents/${uuid}/evaluations/scenarios`,
  GENERATE_SCENARIOS: (uuid: string) =>
    `/admin/agents/${uuid}/evaluations/scenarios/generate`,
  SCENARIO: (uuid: string, scenarioId: string) =>
    `/admin/agents/${uuid}/evaluations/scenarios/${scenarioId}`,
  DUPLICATE_SCENARIO: (uuid: string, scenarioId: string) =>
    `/admin/agents/${uuid}/evaluations/scenarios/${scenarioId}/duplicate`,
  RUNS: (uuid: string) => `/admin/agents/${uuid}/evaluations/runs`,
  RUN: (uuid: string, runId: string) =>
    `/admin/agents/${uuid}/evaluations/runs/${runId}`,
  PROMPT_OPTIMIZATIONS: (uuid: string) =>
    `/admin/agents/${uuid}/evaluations/prompt-optimizations`,
  MANUAL_PROMPT_CANDIDATE: (uuid: string) =>
    `/admin/agents/${uuid}/evaluations/prompt-optimizations/manual`,
  PROMPT_OPTIMIZATION: (uuid: string, candidateId: string) =>
    `/admin/agents/${uuid}/evaluations/prompt-optimizations/${candidateId}`,
  ACCEPT_PROMPT_OPTIMIZATION: (uuid: string, candidateId: string) =>
    `/admin/agents/${uuid}/evaluations/prompt-optimizations/${candidateId}/accept`,
  ROLLBACK_PROMPT_VERSION: (uuid: string, versionId: string) =>
    `/admin/agents/${uuid}/evaluations/prompt-versions/${versionId}/rollback`,
};

// Tag endpoints
export const TAG_ENDPOINTS = {
  LIST: "/tags/",
  GET: (id: number) => `/tags/${id}`,
  CREATE: "/tags/",
  UPDATE: (id: number) => `/tags/${id}`,
  DELETE: (id: number) => `/tags/${id}`,
};

// Language endpoints
export const LANGUAGE_ENDPOINTS = {
  LIST: "/languages/",
  CREATE: "/languages/",
  UPDATE: (id: number) => `/languages/${id}`,
  DELETE: (id: number) => `/languages/${id}`,
  MAPPINGS: "/languages/mappings",
};

// Health check endpoint
export const HEALTH_ENDPOINT = "/health";

// Audio Recordings endpoints
export const AUDIO_RECORDINGS_ENDPOINTS = {
  LIST: "/admin/audio/recordings",
  DOWNLOAD: (filename: string) =>
    `/audio/recordings/${encodeURIComponent(filename)}/download`,
  STREAM: (filename: string) =>
    `/audio/recordings/${encodeURIComponent(filename)}/stream`,
};
